import { getProcess, resolveLatestProcessName } from '../../transactions/transaction';
import { LISTING_STATE_PUBLISHED } from '../../util/types';
import { hasPermissionToPostListings } from '../../util/userHelpers';

/**
 * Everything on this page is derived from the transactions the Inbox already
 * fetches, plus the current user's own listings. Nothing here calls the API.
 */

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// The Marketplace API models a booking's time on the booking entity.
export const bookingStart = tx => {
  const booking = tx?.booking;
  const start = booking?.attributes?.displayStart || booking?.attributes?.start;
  return start ? new Date(start) : null;
};

export const bookingEnd = tx => {
  const booking = tx?.booking;
  const end = booking?.attributes?.displayEnd || booking?.attributes?.end;
  return end ? new Date(end) : null;
};

/** The transaction's process and its current state; nulls when the process is unknown. */
export const processInfo = tx => {
  const processName = resolveLatestProcessName(tx?.attributes?.processName);
  if (!processName) {
    return { process: null, state: null };
  }
  try {
    const process = getProcess(processName);
    return { process, state: process.getState(tx) };
  } catch (e) {
    // An unknown or unsupported process must not take the page down.
    return { process: null, state: null };
  }
};

/** Transactions where the learner has paid and is waiting on accept/decline. */
export const pendingRequests = transactions =>
  transactions
    .filter(tx => {
      const { process, state } = processInfo(tx);
      return !!process && process.statesNeedingProviderAttention?.includes(state);
    })
    .sort((a, b) => (bookingStart(a)?.getTime() || 0) - (bookingStart(b)?.getTime() || 0));

/** Accepted bookings that have not started yet, soonest first. */
export const upcomingLessons = (transactions, now = new Date()) =>
  transactions
    .filter(tx => {
      const { process, state } = processInfo(tx);
      const isAccepted = !!process && state === process.states?.ACCEPTED;
      const start = bookingStart(tx);
      return isAccepted && start && start.getTime() >= now.getTime();
    })
    .sort((a, b) => bookingStart(a).getTime() - bookingStart(b).getTime());

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/**
 * Monday-to-Sunday strip. Each day carries its confirmed lessons (earliest
 * first) so the dashboard can list them when the day is selected.
 */
export const weekStrip = (transactions, now = new Date()) => {
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekdayFromMonday = (monday.getDay() + 6) % 7;
  monday.setDate(monday.getDate() - weekdayFromMonday);

  const counted = transactions.filter(tx => {
    const { process, state } = processInfo(tx);
    return !!process && [process.states?.ACCEPTED, process.states?.DELIVERED].includes(state);
  });

  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(monday.getTime() + i * MS_PER_DAY);
    const lessons = counted
      .filter(tx => {
        const start = bookingStart(tx);
        return start && isSameDay(start, date);
      })
      .sort((a, b) => bookingStart(a).getTime() - bookingStart(b).getTime());
    return { date, lessons, count: lessons.length, isToday: isSameDay(date, now) };
  });
};

/**
 * Details the checkout collected, if Console defines these transaction fields.
 * Every one is optional: a marketplace that does not collect them simply shows
 * fewer rows rather than an empty label.
 *
 * The key names are the ones to use when adding booking fields in Console
 * (Build → Content → Transaction fields).
 */
export const lessonDetails = tx => {
  const protectedData = tx?.attributes?.protectedData || {};
  const customerProtected = tx?.customer?.attributes?.profile?.protectedData || {};

  // A transaction field configured with showTo: 'customer' is saved under a
  // prefixed key — 'pickupAddress' becomes 'customer_pickupAddress'. See
  // getPrefixedKey in util/fieldHelpers.js. Both spellings are read so the
  // dashboard works whichever way the field ends up configured.
  const read = name => protectedData[`customer_${name}`] ?? protectedData[name] ?? null;

  const pickupAddress =
    read('pickupAddress') ||
    read('pickupLocation') ||
    protectedData.shippingDetails?.address?.line1 ||
    null;

  // "I'll meet you" at checkout saves a sentence in the pickup field, not an address.
  const meetsInstructor = read('meetingPoint') === 'meet';

  return {
    pickupAddress,
    focus: read('lessonFocus') || read('focus') || null,
    phoneNumber: read('phoneNumber') || customerProtected.phoneNumber || null,
    // Opens in Apple Maps on iOS and Google Maps elsewhere.
    directionsUrl:
      pickupAddress && !meetsInstructor
        ? `https://maps.google.com/?q=${encodeURIComponent(pickupAddress)}`
        : null,
  };
};

const addMoney = (total, money) => {
  if (!money) {
    return total;
  }
  return { amount: total.amount + money.amount, currency: money.currency || total.currency };
};

/**
 * Earnings for the current Monday-to-Sunday week, from payoutTotal — what the
 * instructor receives after the marketplace commission.
 *
 * Sharetribe does not expose Stripe payout dates through the Marketplace API,
 * so this reports what is earned and what is completed, not when money lands.
 */
export const weekEarnings = (transactions, now = new Date()) => {
  const days = weekStrip(transactions, now);
  const weekStart = days[0].date;
  const weekEnd = new Date(days[6].date.getTime() + MS_PER_DAY);

  const inWeek = transactions.filter(tx => {
    const start = bookingStart(tx);
    return start && start >= weekStart && start < weekEnd;
  });

  let confirmed = { amount: 0, currency: null };
  let completed = { amount: 0, currency: null };
  let confirmedCount = 0;
  let completedCount = 0;

  inWeek.forEach(tx => {
    const { process, state } = processInfo(tx);
    if (!process) {
      return;
    }
    const payout = tx.attributes?.payoutTotal;
    if (state === process.states?.ACCEPTED) {
      confirmed = addMoney(confirmed, payout);
      confirmedCount += 1;
    } else if (state === process.states?.DELIVERED) {
      completed = addMoney(completed, payout);
      completedCount += 1;
      confirmed = addMoney(confirmed, payout);
      confirmedCount += 1;
    }
  });

  return { confirmed, confirmedCount, completed, completedCount };
};

/**
 * An instructor counts as verified once they are allowed to post listings.
 *
 * The marketplace already works this way: access control has
 * `users.requirePermissionToPostListings: true`, so a new instructor cannot
 * list until posting rights are granted in Console — which is the same moment
 * their documents are approved. Reading that permission means verification has
 * exactly one trigger, the one already in the workflow, with nothing extra to
 * remember.
 *
 * It is also the honest signal: it reflects what the instructor can actually
 * do, not a note about what someone intended. An instructor cannot grant it to
 * themselves.
 *
 * (This replaced a `verified_user` metadata field, which needed setting by hand
 * on every instructor in addition to the permission itself.)
 */
export const isVerifiedInstructor = currentUser => hasPermissionToPostListings(currentUser);

/**
 * The four activation steps, in the order Drivelo onboards instructors.
 */
export const activationSteps = (currentUser, ownListings) => {
  const profile = currentUser?.attributes?.profile || {};

  const hasProfileDetails = !!profile.displayName && !!profile.bio;
  const isVerified = isVerifiedInstructor(currentUser);
  const hasPayouts = !!currentUser?.attributes?.stripeConnected;
  const hasPublishedListing = ownListings.some(
    l => l.attributes?.state === LISTING_STATE_PUBLISHED
  );

  const steps = [
    {
      key: 'account',
      title: 'Account created',
      description: "You're in.",
      status: 'done',
    },
    {
      key: 'profile',
      title: 'Profile details',
      description: hasProfileDetails
        ? 'Photo, bio and contact details added.'
        : 'Add a photo and a short bio so learners know who they are booking.',
      status: hasProfileDetails ? 'done' : 'now',
      action: hasProfileDetails
        ? null
        : { label: 'Complete your profile', route: 'AccountSettingsPage' },
    },
    {
      key: 'verification',
      title: 'Drivelo verification',
      // Nothing in signup collects documents, so an unverified instructor may
      // not have sent any yet. Say what is needed and link to the documents
      // form, rather than claiming the documents have arrived. The form itself
      // says when they have already been sent.
      description: isVerified
        ? 'Approved — you can list lessons.'
        : "Send us your instructor authority, WWCC, insurance and vehicle details. We review within 5 business days of receiving everything. Already sent them? We'll email you once you're approved.",
      status: isVerified ? 'done' : 'now',
      action: isVerified
        ? null
        : {
            label: 'Send your documents',
            route: 'InstructorDocumentsPage',
          },
    },
    {
      key: 'payouts',
      title: 'Set up payouts',
      description: hasPayouts
        ? 'Connected. Lesson payments will reach your bank.'
        : 'Connect your bank through Stripe so lesson payments reach you. About 5 minutes.',
      status: hasPayouts ? 'done' : 'now',
      action: hasPayouts ? null : { label: 'Set up payouts', route: 'StripePayoutPage' },
    },
    {
      key: 'listing',
      title: 'Post your first listing',
      description: hasPublishedListing
        ? 'Your listing is live.'
        : isVerified && hasPayouts
        ? 'You are ready — create the listing learners will book.'
        : 'Unlocks once verification and payouts are done.',
      status: hasPublishedListing ? 'done' : isVerified && hasPayouts ? 'now' : 'locked',
      action:
        !hasPublishedListing && isVerified && hasPayouts
          ? { label: 'Post a listing', route: 'NewListingPage' }
          : null,
    },
  ];

  const doneCount = steps.filter(s => s.status === 'done').length;
  return { steps, doneCount, isActivated: hasPublishedListing && hasPayouts && isVerified };
};
